import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchCRMUsers,
    selectAllUsers,
    CRM_STAGES,
    STAGE_CONFIG,
} from '../../redux/slices/crmSlice';
import {
    MagnifyingGlassIcon,
    FunnelIcon,
    ArrowDownTrayIcon,
    EyeIcon,
    PencilIcon,
    XMarkIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ArrowsUpDownIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const CrmUsersList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const allUsers = useSelector(selectAllUsers);

    // State management
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [selectedStages, setSelectedStages] = useState([]);
    const [selectedLeadTypes, setSelectedLeadTypes] = useState([]);
    const [selectedServices, setSelectedServices] = useState([]);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [sortColumn, setSortColumn] = useState('createdAt');
    const [sortDirection, setSortDirection] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    // Available services
    const availableServices = [
        'Weight Gain',
        'Weight Loss',
        'Skin Care',
        'Products',
        'Child Nutrition',
        'General Health'
    ];

    // Fetch users on mount
    useEffect(() => {
        dispatch(fetchCRMUsers());
    }, [dispatch]);

    // Helper to safely get Date object
    const getDateObject = (user, field = 'createdAt') => {
        const val = user[field] || user.api_created_at || user.created_at;
        if (!val) return null;

        try {
            if (val.seconds) {
                return new Date(val.seconds * 1000);
            }
            const date = new Date(val);
            return isNaN(date.getTime()) ? null : date;
        } catch (e) {
            return null;
        }
    };

    // Filter and sort users
    const filteredAndSortedUsers = useMemo(() => {
        let filtered = [...allUsers];

        // Search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(user =>
                user.name?.toLowerCase().includes(query) ||
                user.number?.toLowerCase().includes(query) ||
                user.email?.toLowerCase().includes(query)
            );
        }

        // Stage filter
        if (selectedStages.length > 0) {
            filtered = filtered.filter(user => selectedStages.includes(user.stage));
        }

        // Lead type filter
        if (selectedLeadTypes.length > 0) {
            filtered = filtered.filter(user => {
                const leadType = user.leadType || 'service';
                return selectedLeadTypes.includes(leadType);
            });
        }

        // Services filter
        if (selectedServices.length > 0) {
            filtered = filtered.filter(user => {
                if (!user.services || user.services.length === 0) return false;
                const userServices = Array.isArray(user.services) ? user.services : [user.services];
                return userServices.some(service => selectedServices.includes(service));
            });
        }

        // Date range filter
        if (dateFrom) {
            filtered = filtered.filter(user => {
                const date = getDateObject(user);
                if (!date) return false;
                return date >= new Date(dateFrom);
            });
        }

        if (dateTo) {
            filtered = filtered.filter(user => {
                const date = getDateObject(user);
                if (!date) return false;
                return date <= new Date(dateTo + 'T23:59:59');
            });
        }

        // Sorting
        filtered.sort((a, b) => {
            let aValue, bValue;

            switch (sortColumn) {
                case 'name':
                    aValue = a.name || '';
                    bValue = b.name || '';
                    break;
                case 'mobile':
                    aValue = a.number || '';
                    bValue = b.number || '';
                    break;
                case 'email':
                    aValue = a.email || '';
                    bValue = b.email || '';
                    break;
                case 'stage':
                    aValue = a.stage || '';
                    bValue = b.stage || '';
                    break;
                case 'createdAt':
                    aValue = getDateObject(a) || new Date(0);
                    bValue = getDateObject(b) || new Date(0);
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

        return filtered;
    }, [allUsers, searchQuery, selectedStages, selectedLeadTypes, selectedServices, dateFrom, dateTo, sortColumn, sortDirection]);

    // Pagination
    const totalPages = Math.ceil(filteredAndSortedUsers.length / rowsPerPage);
    const paginatedUsers = filteredAndSortedUsers.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, selectedStages, selectedLeadTypes, selectedServices, dateFrom, dateTo, rowsPerPage]);

    // Handlers
    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const toggleStage = (stage) => {
        setSelectedStages(prev =>
            prev.includes(stage) ? prev.filter(s => s !== stage) : [...prev, stage]
        );
    };

    const toggleLeadType = (type) => {
        setSelectedLeadTypes(prev =>
            prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
        );
    };

    const toggleService = (service) => {
        setSelectedServices(prev =>
            prev.includes(service) ? prev.filter(s => s !== service) : [...prev, service]
        );
    };

    const clearAllFilters = () => {
        setSearchQuery('');
        setSelectedStages([]);
        setSelectedLeadTypes([]);
        setSelectedServices([]);
        setDateFrom('');
        setDateTo('');
    };

    const hasActiveFilters = searchQuery || selectedStages.length > 0 ||
        selectedLeadTypes.length > 0 || selectedServices.length > 0 || dateFrom || dateTo;

    const exportToCSV = () => {
        const headers = ['Name', 'Mobile', 'Email', 'Stage', 'Services', 'Lead Type', 'Created Date'];
        const csvData = filteredAndSortedUsers.map(user => {
            const date = getDateObject(user);
            return [
                user.name || 'N/A',
                user.number || 'N/A',
                user.email || 'N/A',
                STAGE_CONFIG[user.stage]?.title || 'N/A',
                Array.isArray(user.services) ? user.services.join('; ') : (user.services || 'N/A'),
                user.leadType || 'Service',
                date ? date.toLocaleDateString() : 'N/A'
            ];
        });

        const csv = [headers, ...csvData].map(row => row.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `crm-users-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        toast.success('CSV exported successfully!');
    };

    return (
        <div className='min-h-screen bg-slate-50 p-6'>
            <div className='max-w-7xl mx-auto'>
                {/* Header */}
                <div className='bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden mb-6'>
                    <div className='p-4 border-b border-slate-100 bg-slate-50'>
                        <h3 className='text-[10px] font-black text-slate-400 uppercase tracking-widest'>
                            CRM Management
                        </h3>
                    </div>

                    <div className='p-6'>
                        <div className='flex flex-col md:flex-row items-start md:items-center justify-between gap-4'>
                            <div>
                                <h1 className='text-2xl font-black text-slate-900'>CRM Users List</h1>
                                <p className='text-sm text-slate-500 font-medium mt-1'>
                                    Total: {filteredAndSortedUsers.length} of {allUsers.length} users
                                </p>
                            </div>

                            <div className='flex flex-wrap items-center gap-3'>
                                <button
                                    onClick={exportToCSV}
                                    disabled={filteredAndSortedUsers.length === 0}
                                    className='px-4 py-2.5 bg-green-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-2'
                                >
                                    <ArrowDownTrayIcon className='h-4 w-4' />
                                    Export CSV
                                </button>
                                <button
                                    onClick={() => navigate('/admin/crm')}
                                    className='px-4 py-2.5 bg-slate-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-700 transition-all shadow-sm'
                                >
                                    Back to CRM
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className='bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden mb-6'>
                    <div className='p-4 border-b border-slate-100 bg-slate-50'>
                        <h3 className='text-[10px] font-black text-slate-400 uppercase tracking-widest'>
                            Search & Filters
                        </h3>
                    </div>

                    <div className='p-4 space-y-4'>
                        {/* Search Bar and Filter Toggle */}
                        <div className='flex gap-3'>
                            <div className='flex-1 relative'>
                                <MagnifyingGlassIcon className='absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400' />
                                <input
                                    type='text'
                                    placeholder='Search by name, mobile, or email...'
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className='w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent'
                                />
                            </div>

                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`px-4 py-2.5 rounded-xl border font-black text-xs uppercase tracking-widest transition-all flex items-center gap-2 ${showFilters || hasActiveFilters
                                    ? 'bg-purple-600 text-white border-purple-600 shadow-lg'
                                    : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                                    }`}
                            >
                                <FunnelIcon className='h-4 w-4' />
                                <span className='hidden md:inline'>Filters</span>
                                {hasActiveFilters && (
                                    <span className='bg-white text-purple-600 rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-black'>
                                        {[searchQuery, selectedStages.length > 0, selectedLeadTypes.length > 0, selectedServices.length > 0, dateFrom, dateTo].filter(Boolean).length}
                                    </span>
                                )}
                            </button>
                        </div>

                        {/* Filter Panel */}
                        {showFilters && (
                            <div className='bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-4'>
                                <div className='flex items-center justify-between'>
                                    <h4 className='text-[9px] font-black text-slate-400 uppercase tracking-widest'>
                                        Advanced Filters
                                    </h4>
                                    {hasActiveFilters && (
                                        <button
                                            onClick={clearAllFilters}
                                            className='flex items-center gap-1 text-[9px] text-red-600 hover:text-red-700 font-black uppercase tracking-widest'
                                        >
                                            <XMarkIcon className='h-3 w-3' />
                                            Clear All
                                        </button>
                                    )}
                                </div>

                                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                    {/* Date Range */}
                                    <div className='space-y-2'>
                                        <label className='block text-[9px] font-black text-slate-400 uppercase tracking-widest'>
                                            Date Range
                                        </label>
                                        <div className='grid grid-cols-2 gap-2'>
                                            <input
                                                type='date'
                                                value={dateFrom}
                                                onChange={(e) => setDateFrom(e.target.value)}
                                                className='w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500'
                                            />
                                            <input
                                                type='date'
                                                value={dateTo}
                                                onChange={(e) => setDateTo(e.target.value)}
                                                className='w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500'
                                            />
                                        </div>
                                    </div>

                                    {/* Lead Type */}
                                    <div className='space-y-2'>
                                        <label className='block text-[9px] font-black text-slate-400 uppercase tracking-widest'>
                                            Lead Type
                                        </label>
                                        <div className='flex gap-2'>
                                            <button
                                                onClick={() => toggleLeadType('service')}
                                                className={`flex-1 px-3 py-2.5 text-xs font-black uppercase tracking-widest rounded-xl border transition-all ${selectedLeadTypes.includes('service')
                                                    ? 'bg-purple-600 border-purple-600 text-white shadow-lg'
                                                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                                                    }`}
                                            >
                                                Service
                                            </button>
                                            <button
                                                onClick={() => toggleLeadType('business')}
                                                className={`flex-1 px-3 py-2.5 text-xs font-black uppercase tracking-widest rounded-xl border transition-all ${selectedLeadTypes.includes('business')
                                                    ? 'bg-purple-600 border-purple-600 text-white shadow-lg'
                                                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                                                    }`}
                                            >
                                                Business
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Stages */}
                                <div className='space-y-2'>
                                    <label className='block text-[9px] font-black text-slate-400 uppercase tracking-widest'>
                                        Stages
                                    </label>
                                    <div className='flex flex-wrap gap-2'>
                                        {Object.keys(CRM_STAGES).map((stageKey) => {
                                            const stage = CRM_STAGES[stageKey];
                                            const config = STAGE_CONFIG[stage];
                                            return (
                                                <button
                                                    key={stage}
                                                    onClick={() => toggleStage(stage)}
                                                    className={`px-3 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl border transition-all ${selectedStages.includes(stage)
                                                        ? 'bg-purple-600 border-purple-600 text-white shadow-lg'
                                                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                                                        }`}
                                                >
                                                    {config.title}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Services */}
                                <div className='space-y-2'>
                                    <label className='block text-[9px] font-black text-slate-400 uppercase tracking-widest'>
                                        Services
                                    </label>
                                    <div className='flex flex-wrap gap-2'>
                                        {availableServices.map((service) => (
                                            <button
                                                key={service}
                                                onClick={() => toggleService(service)}
                                                className={`px-3 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl border transition-all ${selectedServices.includes(service)
                                                    ? 'bg-purple-600 border-purple-600 text-white shadow-lg'
                                                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                                                    }`}
                                            >
                                                {service}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Data Table */}
                <div className='bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden'>
                    <div className='overflow-x-auto'>
                        <table className='w-full'>
                            <thead className='bg-slate-50 border-b border-slate-200'>
                                <tr>
                                    <th
                                        onClick={() => handleSort('name')}
                                        className='px-4 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors'
                                    >
                                        <div className='flex items-center gap-1'>
                                            Name
                                            <ArrowsUpDownIcon className='h-3 w-3' />
                                        </div>
                                    </th>
                                    <th
                                        onClick={() => handleSort('mobile')}
                                        className='px-4 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors'
                                    >
                                        <div className='flex items-center gap-1'>
                                            Mobile
                                            <ArrowsUpDownIcon className='h-3 w-3' />
                                        </div>
                                    </th>
                                    <th
                                        onClick={() => handleSort('email')}
                                        className='px-4 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors'
                                    >
                                        <div className='flex items-center gap-1'>
                                            Email
                                            <ArrowsUpDownIcon className='h-3 w-3' />
                                        </div>
                                    </th>
                                    <th
                                        onClick={() => handleSort('stage')}
                                        className='px-4 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors'
                                    >
                                        <div className='flex items-center gap-1'>
                                            Stage
                                            <ArrowsUpDownIcon className='h-3 w-3' />
                                        </div>
                                    </th>
                                    <th className='px-4 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-widest'>
                                        Services
                                    </th>
                                    <th className='px-4 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-widest'>
                                        Lead Type
                                    </th>
                                    <th
                                        onClick={() => handleSort('createdAt')}
                                        className='px-4 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors'
                                    >
                                        <div className='flex items-center gap-1'>
                                            Created
                                            <ArrowsUpDownIcon className='h-3 w-3' />
                                        </div>
                                    </th>
                                    <th className='px-4 py-3 text-left text-[10px] font-black text-slate-600 uppercase tracking-widest'>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className='divide-y divide-slate-100'>
                                {paginatedUsers.map((user) => {
                                    const stageConfig = STAGE_CONFIG[user.stage] || STAGE_CONFIG[CRM_STAGES.NEW_LEAD];
                                    const services = Array.isArray(user.services) ? user.services.join(', ') : (user.services || 'N/A');
                                    const date = getDateObject(user);

                                    return (
                                        <tr key={user.id} className='hover:bg-slate-50 transition-colors'>
                                            <td className='px-4 py-3 text-sm font-semibold text-slate-900'>
                                                {user.name || 'N/A'}
                                            </td>
                                            <td className='px-4 py-3 text-sm text-slate-600'>
                                                {user.number || 'N/A'}
                                            </td>
                                            <td className='px-4 py-3 text-sm text-slate-600'>
                                                {user.email || 'N/A'}
                                            </td>
                                            <td className='px-4 py-3'>
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${stageConfig.color} bg-opacity-10`}>
                                                    {stageConfig.title}
                                                </span>
                                            </td>
                                            <td className='px-4 py-3 text-xs text-slate-600 max-w-xs truncate'>
                                                {services}
                                            </td>
                                            <td className='px-4 py-3'>
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${user.leadType === 'business'
                                                    ? 'text-purple-700 bg-purple-100'
                                                    : 'text-blue-700 bg-blue-100'
                                                    }`}>
                                                    {user.leadType || 'Service'}
                                                </span>
                                            </td>
                                            <td className='px-4 py-3 text-xs text-slate-600'>
                                                {date ? date.toLocaleDateString() : 'N/A'}
                                            </td>
                                            <td className='px-4 py-3'>
                                                <div className='flex items-center gap-2'>
                                                    <button
                                                        onClick={() => window.open(`/admin/user-details/${user.id}`, '_blank')}
                                                        className='p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors'
                                                        title='View Details'
                                                    >
                                                        <EyeIcon className='h-4 w-4' />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>


                        {/* Empty State */}
                        {paginatedUsers.length === 0 && (
                            <div className='text-center py-12'>
                                <MagnifyingGlassIcon className='h-12 w-12 text-slate-300 mx-auto mb-3' />
                                <p className='text-slate-500 font-medium'>No users found</p>
                                <p className='text-slate-400 text-sm mt-1'>Try adjusting your search or filters</p>
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {filteredAndSortedUsers.length > 0 && (
                        <div className='border-t border-slate-200 px-4 py-3 bg-slate-50'>
                            <div className='flex flex-col md:flex-row items-center justify-between gap-4'>
                                <div className='flex items-center gap-2'>
                                    <span className='text-sm text-slate-600'>Rows per page:</span>
                                    <select
                                        value={rowsPerPage}
                                        onChange={(e) => setRowsPerPage(Number(e.target.value))}
                                        className='px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500'
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>

                                <div className='flex items-center gap-4'>
                                    <span className='text-sm text-slate-600'>
                                        Page {currentPage} of {totalPages} ({filteredAndSortedUsers.length} total)
                                    </span>

                                    <div className='flex items-center gap-2'>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                            className='p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                                        >
                                            <ChevronLeftIcon className='h-5 w-5' />
                                        </button>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage === totalPages}
                                            className='p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                                        >
                                            <ChevronRightIcon className='h-5 w-5' />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CrmUsersList;
